import React, { useState } from 'react';
import { Collapse, styled } from '@mui/material';
import { ExpandMore } from '@mui/icons-material';
import { faClipboardList } from '@fortawesome/free-solid-svg-icons';
import { Colors } from 'library';
import { Caption } from './Caption';
import { Card } from './Card';
import { Icon } from './Icon';
import { Row } from './Row';
import { Stack } from './Stack';
import { Text } from './Text';

export interface ProfileSectionProps {
    icon: typeof faClipboardList;
    title: string;
    subtitle: string;
    detail?: React.ReactNode;
    isCompleted?: boolean;
    children?: React.ReactNode;
}

export function ProfileSection({ icon, title, subtitle, detail, isCompleted = false, children }: ProfileSectionProps) {
    const [expanded, setExpanded] = useState(false);

    return (
        <Stack gap="sm">
            <Card padding="sm" onClick={() => setExpanded((prev) => !prev)}>
                <Row gap="md" align="center">
                    <Icon icon={icon} size="md" tone={isCompleted ? 'accent' : 'default'} />
                    <Stack grow gap="none">
                        <Text weight="semibold">{title}</Text>
                        <Caption>{subtitle}</Caption>
                    </Stack>
                    {detail && <SectionDetailSlot>{detail}</SectionDetailSlot>}
                    <Chevron $expanded={expanded}>
                        <ExpandMore sx={{ fontSize: 20, color: Colors.mediumGrey }} />
                    </Chevron>
                </Row>
            </Card>
            {children && (
                <Collapse in={expanded} timeout={250} unmountOnExit>
                    <ExpandedContent>{children}</ExpandedContent>
                </Collapse>
            )}
        </Stack>
    );
}

const Chevron = styled('span', { shouldForwardProp: (prop) => prop !== '$expanded' })<{ $expanded: boolean }>(
    ({ $expanded }) => ({
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        transform: $expanded ? 'rotate(0deg)' : 'rotate(-90deg)',
        transition: 'transform 200ms ease',
    }),
);

const ExpandedContent = styled('div')(({ theme }) => ({
    paddingLeft: 16,
    marginLeft: 16,
    borderLeft: `3px solid ${Colors.lightGrey}`,
    [theme.breakpoints.down('md')]: {
        paddingLeft: 0,
    },
}));

const SectionDetailSlot = styled('span')({
    whiteSpace: 'nowrap',
    flexShrink: 0,
});
